import {
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  Flex,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import { logContext } from '@shared/services';
import { LabDocumentPreview } from '@web-app/components/LabDocuments/LabDocumentPreview';
import { EVENT_NAMES, useAnalytics } from '@web-app/context/AnalyticsContext';
import { useWalmartDocuments } from '@web-app/hooks/session-prep/useWalmartDocuments';
import { usePresignedDocumentDownload } from '@web-app/hooks/usePresignedDocumentDownload';
import Image from 'next/image';
import { useCallback, useEffect, useState } from 'react';

interface WalmartInsightsBannerProps {
  patientFirstName: string;
  patientId: string;
  appointmentId: string;
  /** Chakra tabs mount eagerly — only fire "viewed" when the panel is on screen. */
  isVisible: boolean;
}

export const WalmartInsightsBanner = ({
  patientFirstName,
  patientId,
  appointmentId,
  isVisible,
}: WalmartInsightsBannerProps): JSX.Element | null => {
  const { trackEvent } = useAnalytics();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null | undefined>();

  const { documents, isLoading, error, refetch } = useWalmartDocuments({
    patientId,
    appointmentId,
  });

  // Newest first — banner shows one report.
  const insightsDocument = documents[0] ?? null;

  useEffect(() => {
    if (isVisible && insightsDocument?.key) {
      trackEvent(EVENT_NAMES.WALMART_INSIGHTS_BANNER_VIEWED);
    }
  }, [isVisible, insightsDocument?.key, trackEvent]);

  const resolveFreshUrl = useCallback(async () => {
    const result = await refetch();
    const docs = result.data ?? [];
    const match =
      docs.find((entry) => entry.key === insightsDocument?.key) ?? docs[0];
    return match?.url;
  }, [refetch, insightsDocument?.key]);

  const onOpenPdf = useCallback(async () => {
    if (!insightsDocument) return;

    setIsDrawerOpen(true);
    trackEvent(EVENT_NAMES.WALMART_INSIGHTS_DRAWER_OPENED, {
      documentId: insightsDocument.key,
    });

    const isExpired =
      !insightsDocument.expiresAt ||
      new Date(insightsDocument.expiresAt) <= new Date();

    setPreviewUrl(
      isExpired ? ((await resolveFreshUrl()) ?? null) : insightsDocument.url,
    );
  }, [insightsDocument, resolveFreshUrl, trackEvent]);

  const onDismiss = useCallback(() => {
    setIsDrawerOpen(false);
    setPreviewUrl(undefined);
    trackEvent(EVENT_NAMES.WALMART_INSIGHTS_DRAWER_CLOSED, {
      documentId: insightsDocument?.key,
      filename: insightsDocument?.fileName,
    });
  }, [trackEvent, insightsDocument?.key, insightsDocument?.fileName]);

  const refetchDownloadUrl = useCallback(
    async () => ({ data: { downloadUrl: await resolveFreshUrl() } }),
    [resolveFreshUrl],
  );

  const { downloadDocument, openInNewTab } = usePresignedDocumentDownload({
    downloadUrl: insightsDocument?.url,
    expiresAt: insightsDocument?.expiresAt ?? undefined,
    filename: insightsDocument?.fileName,
    refetchDownloadUrl,
  });

  const onDownload = useCallback(async () => {
    trackEvent(EVENT_NAMES.WALMART_INSIGHTS_DOWNLOAD_CLICKED, {
      documentId: insightsDocument?.key,
      filename: insightsDocument?.fileName,
    });
    await downloadDocument();
  }, [
    downloadDocument,
    insightsDocument?.key,
    insightsDocument?.fileName,
    trackEvent,
  ]);

  const onOpenInNewTab = useCallback(async () => {
    trackEvent(EVENT_NAMES.WALMART_INSIGHTS_OPENED_IN_NEW_TAB, {
      documentId: insightsDocument?.key,
      filename: insightsDocument?.fileName,
    });
    await openInNewTab();
  }, [
    openInNewTab,
    insightsDocument?.key,
    insightsDocument?.fileName,
    trackEvent,
  ]);

  const logPdfLoaded = () => {
    if (!insightsDocument) return;

    datadogLogs.logger.info(
      'Walmart shopping insights PDF loaded in call drawer',
      logContext('session-prep', 'walmart-pdf-loaded', {
        detail:
          'Provider opened the Walmart insights drawer and the PDF finished loading',
        patientId,
        appointmentId,
        documentId: insightsDocument.key,
      }),
    );
  };

  if (isLoading) {
    return (
      <>
        <Divider borderColor="border.default" my={6} />
        <Flex
          height="88px"
          align="center"
          justify="center"
          data-testid="walmart-insights-banner-loading"
        >
          <Spinner
            size="lg"
            color="interactive.brand.default"
            data-testid="walmart-insights-loader"
          />
        </Flex>
      </>
    );
  }

  // A mid-session refetch can fail while the cached document is still usable —
  // only surface the error state when there is nothing left to show.
  if (error && !insightsDocument) {
    return (
      <>
        <Divider borderColor="border.default" my={6} />
        <Flex
          height="88px"
          direction="column"
          align="flex-start"
          justify="center"
          gap={3}
          data-testid="walmart-insights-banner-error"
        >
          <Text color="text.error" fontSize="sm">
            Failed to load Walmart shopping insights.
          </Text>
          <Button
            size="sm"
            onClick={() => void refetch()}
            data-testid="walmart-insights-retry"
          >
            Retry
          </Button>
        </Flex>
      </>
    );
  }

  if (!insightsDocument) {
    return null;
  }

  return (
    <>
      <Divider borderColor="border.default" my={6} />
      <Flex
        align="center"
        gap={{ base: 4, md: '50px' }}
        bg="#001e60"
        borderRadius="16px"
        p={6}
        boxShadow="0px 4px 2px rgba(0, 0, 0, 0.1)"
        data-testid="walmart-insights-banner"
      >
        <Flex align="center" gap={4} flex={1} minW={0}>
          <Box flexShrink={0}>
            <Image
              src="/icons/walmart-spark.svg"
              alt=""
              width={34}
              height={38}
              aria-hidden
            />
          </Box>
          <Box minW={0}>
            <Text
              fontSize="sm"
              fontWeight="bold"
              lineHeight="20px"
              color="white"
            >
              See insights about {patientFirstName}&apos;s recent food choices
            </Text>
            <Text
              fontSize="sm"
              fontWeight="normal"
              lineHeight="20px"
              color="white"
              mt="2px"
            >
              View Walmart shopping habits to ground today&apos;s goals.
            </Text>
          </Box>
        </Flex>
        <Button
          flexShrink={0}
          size="sm"
          h="26px"
          px={2}
          py={1}
          bg="white"
          color="text.default"
          border="1px solid"
          borderColor="border.default"
          borderRadius="10px"
          fontSize="xs"
          fontWeight="medium"
          lineHeight="16px"
          _hover={{ bg: 'gray.50' }}
          onClick={onOpenPdf}
          data-testid="walmart-insights-open-pdf"
        >
          Open PDF
        </Button>
      </Flex>

      <Drawer
        isOpen={isDrawerOpen}
        onClose={onDismiss}
        placement="left"
        size="xl"
        trapFocus={false}
        blockScrollOnMount={false}
        autoFocus={false}
      >
        <DrawerContent>
          <DrawerBody p={0}>
            <LabDocumentPreview
              filename={insightsDocument.fileName}
              mimeType="application/pdf"
              downloadUrl={previewUrl ?? undefined}
              isLoadingUrl={isDrawerOpen && previewUrl === undefined}
              onDismiss={onDismiss}
              onDownload={onDownload}
              onOpenInNewTab={onOpenInNewTab}
              onLoaded={logPdfLoaded}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

WalmartInsightsBanner.displayName = 'WalmartInsightsBanner';
